/**
 * Page shell shared by every V2 settings surface.
 *
 * Handles the parts that are 1:1 across `UserSettingsViewV2`,
 * `SiteSettingsViewV2`, `SiteGroupSettingsViewV2`, and
 * `CustomerSettingsViewV2`:
 *
 *   - `AppNav` + `TitleBar` chrome
 *   - View-only banner (when the user can see but not edit any
 *     visible section)
 *   - Sidebar context + sections registration with cleanup on
 *     unmount (`setSettingsContext` / `setSettingsSections` /
 *     `clearSettingsSections`)
 *   - Scroll-spy → `setActiveSectionId` (highlights the rail entry
 *     for whichever section is currently in view)
 *   - `:section` URL param honor (deep-links like
 *     `/settings/alarms` scroll to the matching section once on
 *     mount)
 *
 * Per-page concerns stay outside the shell: `SettingsForm` (with its
 * `initialValues` / `validationSchema` / `onSave` / `readOnly`),
 * the section components themselves, and any per-section disable
 * wrappers.
 *
 * Callers MUST memoize the `sections` prop (`useMemo` keyed on the
 * relevant primitive flags) — the shell uses it as a useEffect
 * dependency, so a fresh array on every render would re-fire the
 * sidebar registration unnecessarily.
 *
 * `withAppNav` defaults to true for the standalone settings routes
 * (User / SiteGroup / Customer). SiteSettingsViewV2 sets it false —
 * that route is nested under SiteDashboardLayout, which already
 * supplies AppNav + TitleBar, so the shell would otherwise double
 * them up. The sidebar section registration below is independent of
 * this flag: LeftNav reads it straight from the settingsNav redux
 * slice regardless of which AppNav instance is on screen.
 */

import { Box, Stack } from '@mui/material';
import React from 'react';
import { useDispatch } from 'react-redux';

import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import {
  clearSettingsSections,
  setActiveSectionId,
  setSettingsContext,
  setSettingsSections,
} from '@sophocles/features/navigation/settingsNavActions';
import useScrollSpy from '@sophocles/layout/useScrollSpy';

export default function SettingsPageShell({
  // AppNav
  page,
  withAppNav = true,
  // TitleBar — pass either `breadcrumbs` (a precomputed crumb list,
  // used by Site / SiteGroup / Customer settings) or `title` +
  // `showBackButton` (used by User settings, which falls through to
  // TitleBar's auto-breadcrumb path).  Both shapes are passed through
  // unchanged; TitleBar prefers `breadcrumbs` when both are present.
  breadcrumbs,
  title,
  showBackButton,
  // Sidebar context
  contextName,
  basePath,
  scroll = true,
  // Sections — drive sidebar registration, scroll-spy, and `:section`
  // URL-param honor.  Memoize at the call site (see file header).
  sections,
  sectionParam,
  // View-only banner
  viewOnly = false,
  viewOnlyMessage,
  // Body
  children,
}) {
  const dispatch = useDispatch();
  const sectionIds = React.useMemo(() => sections.map(s => s.id), [sections]);

  // Sidebar context + sections registration.  Single effect so the
  // cleanup pair (`clearSettingsSections` + `setSettingsContext(null)`)
  // is symmetric — never leave the sidebar with the previous page's
  // entries when navigating away.
  React.useEffect(() => {
    dispatch(setSettingsContext({ context: contextName, basePath, scroll }));
    dispatch(setSettingsSections(sections));
    return () => {
      dispatch(clearSettingsSections());
      dispatch(setSettingsContext(null));
    };
  }, [dispatch, contextName, basePath, scroll, sections]);

  // Scroll-spy → highlight the rail entry currently in view.
  // AppNav owns the overflow:auto scroll container; locate it once
  // the section list is known so the spy attaches to the right
  // element.
  const [scrollRoot, setScrollRoot] = React.useState(null);
  React.useEffect(() => {
    const el = Array.from(document.querySelectorAll('div')).find(d => {
      const s = window.getComputedStyle(d);
      return s.overflowY === 'auto' && d.scrollHeight > d.clientHeight;
    });
    setScrollRoot(el || null);
  }, [sections]);

  const activeSectionId = useScrollSpy(sectionIds, scrollRoot);
  React.useEffect(() => {
    if (activeSectionId) dispatch(setActiveSectionId(activeSectionId));
  }, [activeSectionId, dispatch]);

  // Honor a `:section` URL param on mount so deep links like
  // `/settings/alarms` actually land on the Alarms section.  Runs
  // once per param value — guarded by a ref so subsequent
  // scroll-spy activity (which doesn't touch the URL) can't
  // re-trigger it.  We deliberately do NOT mirror scroll position
  // back into the URL.
  const honoredSectionRef = React.useRef(null);
  React.useEffect(() => {
    if (!sectionParam) return;
    if (honoredSectionRef.current === sectionParam) return;
    const sectionId = `settings-${sectionParam}`;
    if (!sectionIds.includes(sectionId)) return;
    honoredSectionRef.current = sectionParam;
    const tid = setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
    }, 0);
    return () => clearTimeout(tid);
  }, [sectionParam, sectionIds]);

  const body = (
    <Box sx={{ px: 2, pb: 2 }}>
      {withAppNav ? <TitleBar breadcrumbs={breadcrumbs} title={title} showBackButton={showBackButton} /> : null}
      {viewOnly && viewOnlyMessage ? (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'action.hover',
            color: 'text.secondary',
            fontSize: '0.875rem',
          }}
          role="status"
        >
          {viewOnlyMessage}
        </Box>
      ) : null}
      <Stack spacing={3}>{children}</Stack>
    </Box>
  );

  if (!withAppNav) {
    return body;
  }

  return (
    <AppNav notFullHeight page={page}>
      {body}
    </AppNav>
  );
}
